import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, RotateCcw, Search, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { adminService } from "@/api/admin/admin.service";
import {
  TCreateOnboardingQuestionRequest,
  TOnboardingQuestion,
  TUpdateOnboardingQuestionRequest,
} from "@/api/admin/admin.types";
import { QuestionModal } from "@/components/admin/QuestionModal";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";

type TRoleType = "INVESTOR" | "FUNDRAISER";

function SortableQuestionItem({
  question,
  index,
  onEdit,
  onDelete,
  isActive = true,
}: {
  question: TOnboardingQuestion;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
  isActive?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: question.id,
    disabled: !isActive,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : isActive ? 1 : 0.7,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-start gap-4 border-b border-border p-4 ${
        isActive ? "bg-card hover:bg-muted" : "bg-muted hover:bg-muted"
      }`}
    >
      {isActive ? (
        <div
          {...attributes}
          {...listeners}
          className="flex cursor-grab items-center pt-1 active:cursor-grabbing"
        >
          <GripVertical className="h-5 w-5 text-muted-foreground" />
        </div>
      ) : (
        <div className="flex items-center pt-1">
          <div className="h-5 w-5" />
        </div>
      )}

      <div className="flex-1">
        <div className="mb-2 flex items-start gap-2">
          <span className={`text-sm font-medium ${isActive ? "text-muted-foreground" : "text-muted-foreground"}`}>{index + 1}.</span>
          <div className="flex-1">
            <div className="mb-1 flex items-center gap-2">
              <span className={`text-sm font-medium ${isActive ? "text-foreground" : "text-muted-foreground"}`}>Question: </span>
              <span className={`text-sm ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
                {question.question_text}
              </span>
              {!isActive && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">Inactive</span>
              )}
            </div>
            {question.question_description && (
              <div>
                <span className={`text-sm font-medium ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
                  Sub text:{" "}
                </span>
                <span className={`text-sm ${isActive ? "text-muted-foreground" : "text-muted-foreground"}`}>
                  {question.question_description}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={onEdit}
          className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted"
          title="Edit question"
        >
          <RotateCcw className="h-4 w-4 text-muted-foreground" />
        </button>
        {isActive && (
          <button
            onClick={onDelete}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted"
            title="Delete question"
          >
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
      </div>
    </div>
  );
}

type TActiveFilter = "active" | "inactive";

export function OnboardingQuestionsPage() {
  const [questions, setQuestions] = useState<TOnboardingQuestion[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<TOnboardingQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [roleType, setRoleType] = useState<TRoleType>("INVESTOR");
  const [activeFilter, setActiveFilter] = useState<TActiveFilter>("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<TOnboardingQuestion | undefined>(undefined);
  const [questionToDelete, setQuestionToDelete] = useState<TOnboardingQuestion | null>(null);
  const [isDeletingQuestion, setIsDeletingQuestion] = useState(false);
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchQuestions = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params: Parameters<typeof adminService.getOnboardingQuestions>[0] = {
        role_type: roleType,
        limit: 100,
      };

      // Add is_active filter based on activeFilter state
      params.is_active = activeFilter === "active";

      const response = await adminService.getOnboardingQuestions(params);

      if (response.error) {
        const errorMessage = await extractErrorMessage(response.error);
        setError(errorMessage);
        setQuestions([]);
        toast({
          variant: "destructive",
          title: "Failed to load questions",
          description: errorMessage,
        });
      } else if (response.data) {
        const apiData = response.data?.data;
        if (Array.isArray(apiData)) {
          // Transform API data: map question_metadata to metadata
          // The API returns question_metadata but our types expect metadata
          const transformedData: TOnboardingQuestion[] = apiData.map(
            (q: TOnboardingQuestion & { question_metadata?: TOnboardingQuestion["metadata"] }) => ({
              ...q,
              metadata: q.question_metadata ?? q.metadata ?? null,
            })
          );

          // Sort by order field
          const sorted = [...transformedData];
          sorted.sort((a, b) => {
            const orderA = a.order ?? 0;
            const orderB = b.order ?? 0;
            return orderA - orderB;
          });
          setQuestions(sorted);
        } else {
          setQuestions([]);
        }
      } else {
        setQuestions([]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load questions";
      setError(errorMessage);
      setQuestions([]);
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleType, activeFilter]);

  // Filter questions based on search query
  useEffect(() => {
    if (!Array.isArray(questions)) {
      setFilteredQuestions([]);
      return;
    }
    if (!searchQuery.trim()) {
      setFilteredQuestions(questions);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = questions.filter(
        (q) => q?.question_text?.toLowerCase().includes(query) || q?.question_description?.toLowerCase().includes(query)
      );
      setFilteredQuestions(filtered);
    }
  }, [searchQuery, questions]);

  // Separate filtered questions into active and inactive
  const { active: filteredActiveQuestions, inactive: filteredInactiveQuestions } = useMemo(() => {
    if (!Array.isArray(filteredQuestions)) {
      return { active: [], inactive: [] };
    }
    // Treat undefined/null as active (default), only false is inactive
    const active = filteredQuestions.filter((q) => q.is_active !== false);
    const inactive = filteredQuestions.filter((q) => q.is_active === false);

    return { active, inactive };
  }, [filteredQuestions]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    // Only allow dragging active questions
    if (!Array.isArray(filteredActiveQuestions) || filteredActiveQuestions.length === 0) {
      return;
    }

    const oldIndex = filteredActiveQuestions.findIndex((q) => q.id === active.id);
    const newIndex = filteredActiveQuestions.findIndex((q) => q.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Optimistic update - only reorder active questions
    const newActiveQuestions = arrayMove(filteredActiveQuestions, oldIndex, newIndex);
    const newAllQuestions = [
      ...newActiveQuestions,
      ...(Array.isArray(filteredInactiveQuestions) ? filteredInactiveQuestions : []),
    ];
    setFilteredQuestions(newAllQuestions);

    // Update order in backend - only for active questions
    const questionOrders: Record<string, number> = {};
    newActiveQuestions.forEach((q, index) => {
      questionOrders[q.id] = index + 1;
    });

    try {
      const response = await adminService.reorderOnboardingQuestions({
        question_orders: questionOrders,
      });

      if (response.error) {
        // Rollback on error
        setFilteredQuestions(questions);
        const errorMessage = await extractErrorMessage(response.error);
        toast({
          variant: "destructive",
          title: "Failed to reorder questions",
          description: errorMessage,
        });
      } else {
        // Update questions state to match new order
        const updatedAllQuestions = [
          ...newActiveQuestions,
          ...(Array.isArray(filteredInactiveQuestions) ? filteredInactiveQuestions : []),
        ];
        setQuestions(updatedAllQuestions);
        toast({
          variant: "success",
          title: "Questions reordered successfully",
        });
      }
    } catch (err) {
      // Rollback on error
      setFilteredQuestions(questions);
      const errorMessage = err instanceof Error ? err.message : "Failed to reorder questions";
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    }
  };

  const handleOpenModal = (question?: TOnboardingQuestion) => {
    setEditingQuestion(question);
    setIsModalOpen(true);
  };

  const handleSaveQuestion = async (data: TCreateOnboardingQuestionRequest | TUpdateOnboardingQuestionRequest) => {
    if (editingQuestion) {
      // Update existing question
      const response = await adminService.updateOnboardingQuestion(
        editingQuestion.id,
        data as TUpdateOnboardingQuestionRequest
      );
      if (response.error) {
        const errorMessage = await extractErrorMessage(response.error);
        toast({
          variant: "destructive",
          title: "Failed to update question",
          description: errorMessage,
        });
        throw new Error(errorMessage);
      } else {
        toast({
          variant: "success",
          title: "Question updated successfully",
        });
      }
    } else {
      // Create new question
      const createData = {
        ...data,
        order: (Array.isArray(questions) ? questions.length : 0) + 1,
      } as TCreateOnboardingQuestionRequest;
      const response = await adminService.createOnboardingQuestion(createData);
      if (response.error) {
        const errorMessage = await extractErrorMessage(response.error);
        toast({
          variant: "destructive",
          title: "Failed to create question",
          description: errorMessage,
        });
        throw new Error(errorMessage);
      } else {
        toast({
          variant: "success",
          title: "Question created successfully",
        });
      }
    }
    // Refresh questions
    await fetchQuestions();
  };

  const handleDeleteQuestion = (question: TOnboardingQuestion) => {
    setQuestionToDelete(question);
  };

  const confirmDeleteQuestion = async () => {
    if (!questionToDelete) return;

    setIsDeletingQuestion(true);
    try {
      const response = await adminService.deleteOnboardingQuestion(questionToDelete.id);
      if (response.error) {
        const errorMessage = await extractErrorMessage(response.error);
        toast({
          variant: "destructive",
          title: "Failed to delete question",
          description: errorMessage,
        });
      } else {
        toast({
          variant: "success",
          title: "Question deleted successfully",
        });
        setQuestionToDelete(null);
        await fetchQuestions();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to delete question";
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsDeletingQuestion(false);
    }
  };

  const roleTypeLabel = roleType === "INVESTOR" ? "Investor" : "Founder";

  return (
    <div className="h-full overflow-y-auto bg-card p-4 sm:p-6">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground sm:text-3xl">{roleTypeLabel} Onboarding Questions</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Here you can add, edit, delete or arrange your onboarding questions.
              </p>
            </div>

            {/* Role Type Toggle */}
            <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-1">
              <button
                onClick={() => setRoleType("INVESTOR")}
                className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  roleType === "INVESTOR" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Investor
              </button>
              <button
                onClick={() => setRoleType("FUNDRAISER")}
                className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  roleType === "FUNDRAISER" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Founder
              </button>
            </div>
          </div>

          {/* Search and New Button */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="rounded-md border border-border pl-10"
              />
            </div>

            <Button
              onClick={() => handleOpenModal()}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-primary p-0 text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Section Header - always visible */}
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            {activeFilter === "active" ? "Active Questions" : "Inactive Questions"}
          </h2>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-1">
            <button
              onClick={() => setActiveFilter("active")}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                activeFilter === "active" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setActiveFilter("inactive")}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                activeFilter === "inactive" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Inactive
            </button>
          </div>
        </div>

        {/* Questions List */}
        {isLoading ? (
          <div
            aria-busy="true"
            aria-label="Loading questions"
            className="rounded-lg border border-border bg-card"
          >
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="flex items-start gap-4 border-b border-border p-4 last:border-b-0"
              >
                <Skeleton className="mt-1 h-5 w-5 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <Skeleton className="h-8 w-8 rounded-full" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-destructive">Error: {error}</p>
          </div>
        ) : (
          <>
            {activeFilter === "active" ? (
              Array.isArray(filteredActiveQuestions) && filteredActiveQuestions.length > 0 ? (
                <div className="rounded-lg border border-border bg-card">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => {
                      void handleDragEnd(event);
                    }}
                  >
                    <SortableContext
                      items={Array.isArray(filteredActiveQuestions) ? filteredActiveQuestions.map((q) => q.id) : []}
                      strategy={verticalListSortingStrategy}
                    >
                      {filteredActiveQuestions.map((question, index) => (
                        <SortableQuestionItem
                          key={question.id}
                          question={question}
                          index={index}
                          isActive={true}
                          onEdit={() => handleOpenModal(question)}
                          onDelete={() => handleDeleteQuestion(question)}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
              ) : (
                <div className="flex items-center justify-center rounded-lg border border-border bg-card py-12">
                  <p className="text-muted-foreground">
                    {searchQuery
                      ? "No active questions found matching your search."
                      : "No active questions yet. Click the + button to add one."}
                  </p>
                </div>
              )
            ) : Array.isArray(filteredInactiveQuestions) && filteredInactiveQuestions.length > 0 ? (
              <div className="rounded-lg border border-border bg-muted">
                {filteredInactiveQuestions.map((question, index) => (
                  <SortableQuestionItem
                    key={question.id}
                    question={question}
                    index={index}
                    isActive={false}
                    onEdit={() => handleOpenModal(question)}
                    onDelete={() => handleDeleteQuestion(question)}
                  />
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center rounded-lg border border-border bg-muted py-12">
                <p className="text-muted-foreground">
                  {searchQuery
                    ? "No inactive questions found matching your search."
                    : "No inactive questions yet. Click the + button to add one."}
                </p>
              </div>
            )}
          </>
        )}

        {/* Question Modal */}
        <QuestionModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          question={editingQuestion}
          roleType={roleType}
          onSave={handleSaveQuestion}
        />

        {/* Delete confirmation dialog */}
        <ConfirmDialog
          open={questionToDelete !== null}
          onOpenChange={(open) => { if (!open) setQuestionToDelete(null); }}
          title="Delete question?"
          description={
            questionToDelete
              ? `This will permanently delete: "${questionToDelete.question_text}". This action cannot be undone.`
              : undefined
          }
          confirmLabel="Delete"
          variant="destructive"
          isConfirming={isDeletingQuestion}
          onConfirm={confirmDeleteQuestion}
        />
      </div>
    </div>
  );
}
